import { PageViewEvent } from '../events/page-view-event.mjs';
import { CustomEvent } from '../events/custom-event.mjs';
import { IdentityEvent } from '../events/identity-event.mjs';
import { Personalizer } from '../personalization/personalizer.mjs';
import { CallFlowCDPClient } from '../personalization/callflow-cdp-client.mjs';
import { cookieExists } from '../utils/cookies/cookie-utils.mjs';
import { replaceObsoleteCookieNamePrefixes, createCookie } from '../cookies/browser-cookie-handler.mjs';
import { createSettings, updatePointOfSale } from '../settings/settings.mjs';
import { EventApiClient } from '../cdp/EventApiClient.mjs';
import { loadPlugins } from './extensions/load-plugins.mjs';
import { getBrowserId } from '../cookies/get-browser-id.mjs';
import { Infer } from '../infer/infer.mjs';
import { getGuestId } from '../utils/get-guest-id.mjs';
import { EventQueue } from '../eventStorage/eventStorage.mjs';
import { API_VERSION, LIBRARY_VERSION } from '../utils/consts.mjs';

// © Sitecore Corporation A/S. All rights reserved. Sitecore® is a registered trademark of Sitecore Corporation A/S.
/**
 * Initiates the Engage library using the global settings added by the developer
 * @param settingsInput - Global settings added by the developer
 * @returns A promise that resolves with an object that handles the library functionality
 */
async function init(settingsInput) {
    if (typeof window === 'undefined') {
        throw new Error(
        // eslint-disable-next-line max-len
        `[IE-0001] The "window" object is not available on the server side. Use the "window" object only on the client side, and in the correct execution context.`);
    }
    const settings = createSettings(settingsInput);
    if (!settings.cookieSettings.forceServerCookieMode)
        replaceObsoleteCookieNamePrefixes(settings.cookieSettings);
    if (!settings.cookieSettings.forceServerCookieMode &&
        !cookieExists(window.document.cookie, settings.cookieSettings.cookieName)) {
        await createCookie(settings.targetURL, settings.clientKey, settings.cookieSettings);
    }
    const id = getBrowserId(settings.cookieSettings.cookieName);
    const eventApiClient = new EventApiClient(settings.targetURL, API_VERSION);
    window.Engage = {};
    await loadPlugins(settingsInput);
    window.Engage = {
        ...window.Engage,
        getBrowserId: () => getBrowserId(settings.cookieSettings.cookieName),
        version: LIBRARY_VERSION,
    };
    const infer = new Infer();
    const queue = new EventQueue(sessionStorage, eventApiClient, infer);
    const callFlowCDPClient = new CallFlowCDPClient(settings);
    return {
        addToEventQueue: (type, eventData, extensionData) => {
            const queueEventPayload = {
                eventData,
                extensionData,
                id,
                settings,
                type,
            };
            queue.enqueueEvent(queueEventPayload);
        },
        clearEventQueue: () => {
            queue.clearQueue();
        },
        event: (type, eventData, extensionData) => new CustomEvent({
            eventApiClient,
            eventData,
            extensionData,
            id,
            infer,
            settings,
            type,
        }).send(),
        form: (formId, interactionType, pointOfSale) => {
            const undefinedInfer = {
                language: () => undefined,
                pageName: () => undefined,
            };
            return new CustomEvent({
                eventApiClient,
                eventData: { pointOfSale },
                extensionData: {
                    formId,
                    interactionType: interactionType.toUpperCase(),
                },
                id,
                infer: undefinedInfer,
                settings,
                type: 'FORM',
            }).send();
        },
        getBrowserId: () => getBrowserId(settings.cookieSettings.cookieName),
        getGuestId: () => getGuestId(id, settings.targetURL, settings.clientKey),
        identity: (eventData, extensionData) => new IdentityEvent({
            eventApiClient,
            eventData,
            extensionData,
            id,
            infer,
            settings,
        }).send(),
        pageView: (eventData, extensionData) => new PageViewEvent({
            eventApiClient,
            eventData,
            extensionData,
            id,
            infer,
            searchParams: window.location.search,
            settings,
        }).send(),
        personalize: (personalizeData, timeout) => {
            return new Personalizer(callFlowCDPClient, id, infer).getInteractiveExperienceData(personalizeData, timeout);
        },
        processEventQueue: () => queue.sendAllEvents(),
        updatePointOfSale: (pointOfSale) => updatePointOfSale(pointOfSale, settings),
        version: LIBRARY_VERSION,
    };
}

export { init };
